export interface InstalledModuleRecord {
  id: string;
  name: string;
  version: string;
  bundleUrl: string;
  styleUrl?: string;
  category?: string;
  tier?: 'public' | 'staging';
  status: 'active' | 'disabled';
  settingsSchema?: Record<string, any>;
}

const moduleRegistry = new Map<string, InstalledModuleRecord>();

/**
 * Hydrates the registry dynamically from the backend catalog endpoint.
 * The server inspects Host / X-Forwarded-Host headers to return
 * public utilities on dev.herx.org vs all staging modules on hdev.herx.org.
 */
export async function syncModuleCatalog(baseOrigin = ''): Promise<InstalledModuleRecord[]> {
  const origin = baseOrigin || (typeof window !== 'undefined' ? window.location.origin : '');
  const url = `${origin}/api/modules`;

  try {
    const res = await fetch(url, {
      headers: { Accept: 'application/json' },
    });

    if (!res.ok) {
      throw new Error(`Catalog fetch failed: HTTP ${res.status}`);
    }

    const data = await res.json();
    const modules: any[] = data.modules || [];

    moduleRegistry.clear();
    for (const m of modules) {
      registerModule({
        id: m.id,
        name: m.name,
        version: m.version,
        bundleUrl: m.entry.startsWith('http') ? m.entry : `${origin}${m.entry}`,
        styleUrl: m.styles ? (m.styles.startsWith('http') ? m.styles : `${origin}${m.styles}`) : undefined,
        category: m.category,
        tier: m.tier,
        status: 'active',
      });
    }

    return listActiveModules();
  } catch (err) {
    console.warn('[ModuleRegistry] Falling back to default offline catalog:', err);
    initFallbackModules(origin);
    return listActiveModules();
  }
}

export function registerModule(record: InstalledModuleRecord): void {
  moduleRegistry.set(record.id, record);
}

export function unregisterModule(moduleId: string): boolean {
  return moduleRegistry.delete(moduleId);
}

export function getModuleRecord(moduleId: string): InstalledModuleRecord | undefined {
  return moduleRegistry.get(moduleId);
}

export function getModuleUrl(moduleId: string): string | null {
  return moduleRegistry.get(moduleId)?.bundleUrl ?? null;
}

export function listActiveModules(): InstalledModuleRecord[] {
  return Array.from(moduleRegistry.values()).filter((m) => m.status === 'active');
}

const DEFAULT_DISABLED_MODULES = [
  // Full package IDs
  'org.herx.wall',
  'org.herx.kanban',
  'org.herx.whiteboard',
  'org.herx.scrum',
  'org.herx.crm',
  'org.herx.door-release',
  'org.herx.gallery',
  'org.herx.trader',
  // Short IDs
  'mod-wall',
  'mod-kanban',
  'mod-whiteboard',
  'mod-scrum',
  'mod-crm',
  'mod-door-release',
  'mod-gallery',
  'mod-trader',
];

export function isModuleEnabled(moduleId: string): boolean {
  if (typeof window === 'undefined') return moduleId === 'mod-chat' || moduleId === 'org.herx.chat';
  try {
    const raw = localStorage.getItem('herx_disabled_modules');
    const disabled: string[] = raw ? JSON.parse(raw) : DEFAULT_DISABLED_MODULES;
    
    // Check direct ID or mapped short/long equivalents
    const isExplicitlyDisabled = disabled.includes(moduleId) || 
      disabled.includes(moduleId.replace('org.herx.', 'mod-')) ||
      disabled.includes(moduleId.replace('mod-', 'org.herx.'));
      
    return !isExplicitlyDisabled;
  } catch {
    return moduleId === 'mod-chat' || moduleId === 'org.herx.chat';
  }
}

export function toggleModule(moduleId: string, enabled: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    const raw = localStorage.getItem('herx_disabled_modules');
    let disabled: string[] = raw ? JSON.parse(raw) : [...DEFAULT_DISABLED_MODULES];
    
    const altId1 = moduleId.replace('org.herx.', 'mod-');
    const altId2 = moduleId.replace('mod-', 'org.herx.');
    
    if (enabled) {
      disabled = disabled.filter((id) => id !== moduleId && id !== altId1 && id !== altId2);
    } else {
      if (!disabled.includes(moduleId)) disabled.push(moduleId);
      
      // Strip active styles for this module
      const styleTag = document.getElementById(`herx-style-${moduleId}`);
      if (styleTag) {
        styleTag.remove();
      }
    }
    localStorage.setItem('herx_disabled_modules', JSON.stringify(disabled));
    window.dispatchEvent(new CustomEvent('module-toggled', { detail: { moduleId, enabled } }));
    
    // Also notify AppShell / plugins
    window.dispatchEvent(new CustomEvent('herx:modules-changed', { detail: { moduleId, enabled } }));
  } catch (err) {
    console.warn('[ModuleRegistry] Failed to toggle module state:', err);
  }
}

function initFallbackModules(origin: string): void {
  registerModule({
    id: 'org.herx.wall',
    name: 'Sovereign Wall',
    version: '1.0.0',
    bundleUrl: `${origin}/modules/wall/dist/bundle.js`,
    styleUrl: `${origin}/modules/wall/dist/style.css`,
    category: 'social',
    tier: 'staging',
    status: 'active',
  });
  registerModule({
    id: 'org.herx.kanban',
    name: 'Action Planner & Kanban',
    version: '1.0.0',
    bundleUrl: `${origin}/modules/kanban/dist/bundle.js`,
    styleUrl: `${origin}/modules/kanban/dist/style.css`,
    category: 'productivity',
    tier: 'staging',
    status: 'active',
  });
  registerModule({
    id: 'org.herx.whiteboard',
    name: 'Collaborative Whiteboard',
    version: '1.0.0',
    bundleUrl: `${origin}/modules/whiteboard/dist/bundle.js`,
    styleUrl: `${origin}/modules/whiteboard/dist/style.css`,
    category: 'collaboration',
    tier: 'staging',
    status: 'active',
  });
  registerModule({
    id: 'org.herx.scrum',
    name: 'Scrum & Agile Master',
    version: '1.0.0',
    bundleUrl: `${origin}/modules/scrum/dist/bundle.js`,
    styleUrl: `${origin}/modules/scrum/dist/style.css`,
    category: 'productivity',
    tier: 'staging',
    status: 'active',
  });
  registerModule({
    id: 'org.herx.crm',
    name: 'CRM & Pipeline Hub',
    version: '1.0.0',
    bundleUrl: `${origin}/modules/crm/dist/bundle.js`,
    styleUrl: `${origin}/modules/crm/dist/style.css`,
    category: 'productivity',
    tier: 'staging',
    status: 'active',
  });
  registerModule({
    id: 'org.herx.chat',
    name: 'Encrypted Chat & Channels',
    version: '1.0.0',
    bundleUrl: `${origin}/modules/chat/dist/bundle.js`,
    styleUrl: `${origin}/modules/chat/dist/style.css`,
    category: 'communication',
    tier: 'staging',
    status: 'active',
  });
  registerModule({
    id: 'org.herx.trader',
    name: 'Trader Desk & TCA Telemetry',
    version: '1.0.0',
    bundleUrl: `${origin}/modules/trader/dist/bundle.js`,
    styleUrl: `${origin}/modules/trader/dist/style.css`,
    category: 'finance',
    tier: 'staging',
    status: 'active',
  });
}

if (typeof window !== 'undefined') {
  (window as any).__herx_test_registerModule = registerModule;
}
